import React from "react";
import {
    displayCategory,
    displayCurrentRole,
    displayFormType,
    displayIpLocation,
    displayText,
    formatEnquiryDate,
    parseEnquiryMore,
    type EnquiryRow,
} from "./enquiry-helpers";
import { EnquiryHrPanel } from "./EnquiryHrPanel";
import { EnquiryLinkedInLink } from "./EnquiryLinkedInLink";
import { EnquiryResumeDownloadButton } from "./EnquiryResumeDownloadButton";

type DetailField = { label: string; value: React.ReactNode };

const DetailSection = ({ title, fields }: { title: string; fields: DetailField[] }) => (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-violet-700">{title}</h3>
        <dl className="space-y-3">
            {fields.map((field) => (
                <div key={field.label}>
                    <dt className="text-xs font-medium text-gray-500">{field.label}</dt>
                    <dd className="mt-0.5 break-words text-sm text-gray-900">{field.value}</dd>
                </div>
            ))}
        </dl>
    </section>
);

const buildEnquiryDetailSections = (row: EnquiryRow) => {
    const more = parseEnquiryMore(row);

    return [
        {
            title: "Contact",
            fields: [
                { label: "Name", value: displayText(row.name) },
                { label: "Phone", value: displayText(row.phoneno) },
                { label: "Email", value: displayText(row.email) },
                { label: "Address", value: displayText(row.location) },
            ],
        },
        {
            title: "Submission",
            fields: [
                { label: "Date", value: formatEnquiryDate(row.timestamp) },
                { label: "Form Type", value: displayFormType(row) },
                { label: "Category", value: displayCategory(row) },
                { label: "Current Role", value: displayCurrentRole(row) },
                { label: "Experience", value: displayText(more.experience) },
                {
                    label: "LinkedIn",
                    value: more.linkedin ? <EnquiryLinkedInLink url={more.linkedin} /> : "-",
                },
                {
                    label: "Resume",
                    value: <EnquiryResumeDownloadButton row={row} />,
                },
            ],
        },
        {
            title: "Message",
            fields: [{ label: "Content", value: displayText(row.message) }],
        },
        {
            title: "Technical",
            fields: [
                { label: "IP Address", value: displayText(row.ip) },
                { label: "IP Location", value: displayIpLocation(row.reversedip) },
                { label: "Browser", value: displayText(row.browser) },
            ],
        },
    ];
};

export const EnquiryDetailSections = ({
    row,
    refreshTable,
}: {
    row: EnquiryRow;
    refreshTable: () => void;
}) => (
    <div className="space-y-4">
        <EnquiryHrPanel row={row} refreshTable={refreshTable} />
        {buildEnquiryDetailSections(row).map((section) => (
            <DetailSection key={section.title} title={section.title} fields={section.fields} />
        ))}
    </div>
);
